import axios from 'axios';
import { z } from 'zod';
import { toast } from '../../components/ui/use-toast';
import { CircleCheckBig } from 'lucide-react';
import { formatDateToYYYYMMDDLocal } from '../../utils/dateUtils';
const baseUrl = process.env.REACT_APP_SERVER_URL;

export const getAllNews = async () => {
  try {
    const response = await axios.get(`${baseUrl}/api/news`);
    return { news: response.data.news };
  } catch (error) {
    console.error(error);
  }
};

export const getAllPaginatedNews = async (page: number) => {
  try {
    const response = await axios.get(`${baseUrl}/api/news/all?page=${page}`);
    return {
      news: response.data.news,
      count: response.data.count,
      totalPages: response.data.totalPages,
    };
  } catch (error) {
    console.error(error);
  }
};

export const getNewsBySlug = async (slug: string) => {
  try {
    const response = await axios.get(`${baseUrl}/api/news/slug/${slug}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const handleCreateNews = async (formData: any, navigate: Function, isDraft = false) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'multipart/form-data',
      },
    };

    const newsFormData = new FormData();

    for (const key in formData) {
      // "image" et "isDraft" sont gérés séparément ci-dessous
      if (formData.hasOwnProperty(key) && key !== 'image' && key !== 'isDraft') {
        if (formData[key] !== undefined && formData[key] !== null) {
          if (Array.isArray(formData[key])) {
            newsFormData.append(key, JSON.stringify(formData[key]));
          } else if (key === 'date' && formData[key] instanceof Date) {
            const dateStr = formatDateToYYYYMMDDLocal(formData[key]);
            newsFormData.append(key, dateStr);
          } else {
            newsFormData.append(key, formData[key]);
          }
        }
      }
    }

    // isDraft : on utilise la valeur du formulaire, pas le paramètre externe
    newsFormData.append('isDraft', String(!!(formData as any).isDraft));

    if (formData.image && Array.isArray(formData.image)) {
      formData.image.forEach((image: any) => {
        if (image.file) {
          newsFormData.append(`image`, image.file);
        }
      });
    }

    const response = await axios.post(`${baseUrl}/api/news/`, newsFormData, config);

    toast({
      variant: 'destructive',
      className: 'bg-green-500 border border-green-500',
      description: (
        <div className="flex items-center">
          <CircleCheckBig className="mr-2" />
          <span className="first-letter:capitalize">{response.data.msg}</span>
        </div>
      ),
    });
    navigate('/admin-dashboard/actualités');
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const fieldErrors: { [key: string]: string } = {};
      error.errors.forEach((err: any) => {
        fieldErrors[err.path[0]] = err.message;
      });
    } else {
      if (error.response && error.response.data && error.response.data.message) {
        toast({
          variant: 'destructive',
          title: "Une erreur s'est produite.",
          description: error.response.data.message,
          className: 'bg-red-500 border border-red-500',
        });
        return error.response.data.message;
      }
    }
  }
};

export const handleUpdateNews = async (
  id: number,
  formData: any,
  navigate: Function,
  isDraft = false
) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'multipart/form-data',
      },
    };

    const formDataToSend = new FormData();
    formDataToSend.append('title', formData.title);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('isDraft', String(isDraft));

    if (formData.date instanceof Date) {
      const dateStr = formatDateToYYYYMMDDLocal(formData.date);
      formDataToSend.append('date', dateStr);
    } else {
      formDataToSend.append('date', formData.date);
    }

    // N'envoyer l'image que si c'est un vrai fichier File (pas un objet reconstruit depuis la BDD)
    if (formData.image && formData.image[0]?.file instanceof File) {
      formDataToSend.append('image', formData.image[0].file);
    }

    const response = await axios.patch(`${baseUrl}/api/news/${id}`, formDataToSend, config);

    toast({
      variant: 'destructive',
      title: 'Opération réussie.',
      description: response.data.msg,
      className: 'bg-green-500 border border-green-500',
    });
    navigate('/admin-dashboard/actualités');
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const fieldErrors: { [key: string]: string } = {};
      error.errors.forEach((err: any) => {
        fieldErrors[err.path[0]] = err.message;
      });
    } else {
      if (error.response && error.response.data && error.response.data.message) {
        toast({
          variant: 'destructive',
          title: "Une erreur s'est produite.",
          description: error.response.data.message,
          className: 'bg-red-500 border border-red-500',
        });
        return error.response.data.message;
      }
    }
  }
};

export const deleteNews = async (id: number) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    };
    const response = await axios.delete(`${baseUrl}/api/news/${id}`, config);
    toast({
      variant: 'destructive',
      className: 'bg-green-500 border border-green-500',
      description: (
        <div className="flex items-center">
          <CircleCheckBig className="mr-2" />
          <span className="first-letter:capitalize">{response.data.message}</span>
        </div>
      ),
    });

    return response;
  } catch (error) {
    console.error(error);
  }
};

export const toggleDraftNews = async (id: number) => {
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    };
    const response = await axios.patch(`${baseUrl}/api/news/${id}/toggle-draft`, {}, config);
    toast({
      variant: 'destructive',
      className: 'bg-green-500 border border-green-500',
      description: (
        <div className="flex items-center">
          <CircleCheckBig className="mr-2" />
          <span className="first-letter:capitalize">{response.data.msg}</span>
        </div>
      ),
    });
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const getNewsById = async (id: number) => {
  try {
    const response = await axios.get(`${baseUrl}/api/news/${id}`);
    return response.data;
  } catch (error) {
    console.error(error);
  }
};
